import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const STORAGE_KEY = "joh_auth_token";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = unknown, false = anonymous, object = signed in
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || null);

  const clear = useCallback(() => {
    // Was de adminsessie hetzelfde token (admin via /login ingelogd)? Dan ook daar uitloggen.
    const cur = localStorage.getItem(STORAGE_KEY);
    if (cur && localStorage.getItem("joh_admin_token") === cur) {
      localStorage.removeItem("joh_admin_token");
    }
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(false);
  }, []);

  const applyToken = useCallback(async (t, u) => {
    localStorage.setItem(STORAGE_KEY, t);
    setToken(t);
    let resolved = u;
    if (resolved) setUser(resolved);
    else {
      try { const { data } = await api.get("/auth/me"); setUser(data); resolved = data; }
      catch { clear(); return; }
    }
    // Admin/owner-accounts blijven ook in /admin en JACE ingelogd (één gedeelde sessie)
    if (["admin", "owner"].includes(resolved?.role)) localStorage.setItem("joh_admin_token", t);
  }, [clear]);

  useEffect(() => {
    let cancelled = false;
    // CRITICAL: when returning from Google OAuth the URL carries #session_id=...
    // Skip the /me check — AuthCallback exchanges the session_id and signs in first.
    if (window.location.hash?.includes("session_id=")) return;
    (async () => {
      if (!token) { setUser(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) clear();
      }
    })();
    return () => { cancelled = true; };
  }, [token, clear]);

  const signIn = useCallback(async (email, password, remember_me) => {
    const { data } = await api.post("/auth/login", { email, password, remember_me });
    await applyToken(data.token, data.user);
    return data.user;
  }, [applyToken]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    await applyToken(data.token, data.user);
    return data.user;
  }, [applyToken]);

  const setPassword = useCallback(async (payload) => {
    const { data } = await api.post("/auth/set-password", payload);
    await applyToken(data.token, data.user);
    return data.user;
  }, [applyToken]);

  const signOut = useCallback(() => {
    api.post("/auth/logout").catch(() => {}); // clears Google session cookie server-side
    clear();
  }, [clear]);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, signIn, register, setPassword, applyGoogleSession: applyToken, signOut, refreshUser, ready: user !== null }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function formatApiError(detail) {
  if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
